import Images from '../Utils/images';
import CustomButton from './CustomButton';

interface Props {
  heading?: string;
  title?: string;
  buttonText?: string;
  showButton?: boolean;
  handleClick?: () => void;
}

/**
 * Renders a component for displaying a 'No Internet' message.
 * @example
 * NoInternet({ heading: "No Internet", title: "Please check your connection", buttonText: "Retry", handleClick: myCallback, showButton: true })
 * Returns a JSX element displaying the message and button.
 * @param {Props} props - The props object containing necessary data for displaying the component:
 *   @param {string} props.heading - The heading text displayed on the component.
 *   @param {string} props.title - The title text displayed below the heading.
 *   @param {string} props.buttonText - The text to be displayed on the button.
 *   @param {function} props.handleClick - The callback function to be triggered on button click.
 *   @param {boolean} props.showButton - Determines if the button should be displayed.
 * @returns {JSX.Element} The rendered JSX of the NoInternet component.
 * @description
 *   - The component uses a static image from Images.NO_INTERNET for illustration.
 *   - The button is displayed only if `showButton` is true.
 *   - CustomButton receives predefined styles for a consistent UI appearance.
 */
export default function NoInternet(props: Props) {
  const { heading, title, buttonText, handleClick, showButton } = props;
  return (
    <div className="success noInternet">
      <img src={Images.NO_INTERNET} alt={heading} />
      <h3>{heading}</h3>
      <p>{title}</p>
      {showButton ? (
        <CustomButton
          size="large"
          variant="contained"
          text={buttonText as string}
          showIcon={false}
          width="auto"
          onClick={handleClick}
          id="redirect"
          loading={false}
          className="no_internet_button"
        />
      ) : null}
    </div>
  );
}
